import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { JSX, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { currentTenantId } from '../../../library/brandingCache';
import { useValidatedForm } from '../../../library/formValidation';
import { splitApiError } from '../../../library/useApiFormError';
import { ClinicProgramDraft, IClinicProgram, IProgramDay, toClinicProgramDraft, toClinicProgramRequest } from '../../../library/models/clinicProgram';
import { toTimeInputValue } from '../../../library/dateTimeHelper';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { CLINIC_WEEK_COPY, ProgramWeekEditor } from '../../program/ProgramWeekEditor';
import { createProgramWeekSchema, ProgramWeekFormValues } from '../../program/programWeekSchema';
import { actionsStyles, blockedSaveStyles, cardHintStyles, cardStyles, cardTitleStyles, sectionStyles } from './workingHoursSettings.styles';

/**
 * Where a clinic says when it is open.
 *
 * Seven rows, each a day that is either shut or open between two times. This is the screen the
 * platform owner used to stand in for: a clinic's hours are its own operational detail, they
 * change without anybody outside hearing about it, and the person who knows them is the person
 * sitting here.
 *
 * What this governs is what **patients** can book, and the description says so: the clinic's
 * own staff can still put something in the diary outside these hours, because an emergency at
 * eight in the evening is a real thing and a screen that fought them would be the wrong answer
 * to it.
 *
 * It is also the ceiling on every therapist's own hours. A therapist's program can never exceed
 * the clinic's, so shortening a day here shortens it for whoever was working past the new
 * closing time -- and the save answers with their names, which is what the warning below is.
 * An administrator finding that out from the therapist a week later would be the alternative.
 *
 * The seven rows themselves are `ProgramWeekEditor`, shared with the therapist's own screen.
 */
/**
 * The stored hours as the time picker reads them.
 *
 * The API stores `HH:mm:ss` and the picker speaks `HH:mm`; typing has always sent back
 * whatever the field held, so normalising once on the way in keeps the two in step without
 * a conversion on every keystroke.
 */
const normalisedDraft = (draft: ClinicProgramDraft): ClinicProgramDraft =>
    draft.map((day: IProgramDay) => ({
        ...day,
        opensAt: toTimeInputValue(day.opensAt),
        closesAt: toTimeInputValue(day.closesAt),
    }));

export const WorkingHoursSettings = (): JSX.Element => {
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    // Keyed by tenant, or somebody in two clinics is served the first one's hours.
    const { data, isLoading, refetch } = useReactQuery<IClinicProgram>(
        services.clinicProgram.getProgram(currentTenantId())
    );

    const saveProgram = useReactMutation(services.clinicProgram.saveProgramMutation());

    // No bounds: the clinic's own hours are capped by nothing.
    const schema = useMemo(() => createProgramWeekSchema(translate), [translate]);

    const {
        control, handleSubmit, reset, setValue, setError,
        formState: { errors, isValid },
    } = useValidatedForm<ProgramWeekFormValues>(schema, {
        defaultValues: { days: toClinicProgramDraft(undefined) },
    });

    /** Reseeded from the server, so the fields open on what is actually happening. */
    useEffect(() => {
        if (data) {
            reset({ days: normalisedDraft(toClinicProgramDraft(data)) });
        }
    }, [data, reset]);

    const current: ClinicProgramDraft =
        (useWatch({ control }) as ProgramWeekFormValues).days ?? [];

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);

    const onValid = (values: ProgramWeekFormValues): void => {
        saveProgram.mutate(toClinicProgramRequest(values.days), {
            onSuccess: (saved: IClinicProgram) => {
                showSnackbar(translate(ResourcesIds.WORKING_HOURS_SAVE_SUCCESS), 'success');

                // Not an error: the save worked and the narrowing is correct. It is the one
                // consequence of this screen that happens to somebody else's, so it is said
                // out loud while the administrator can still change their mind.
                const narrowed: string[] = saved.narrowedTherapists ?? [];

                if (narrowed.length > 0) {
                    showSnackbar(
                        translate(ResourcesIds.WORKING_HOURS_THERAPISTS_NARROWED,
                            { names: narrowed.join(', ') }),
                        'warning'
                    );
                }

                refetch();
            },
            onError: (error: Error) => {
                const outcome = splitApiError(error, knownFields, translate,
                    ResourcesIds.WORKING_HOURS_SAVE_ERROR);

                Object.entries(outcome.fieldErrors).forEach(([field, message]) =>
                    setError(field as keyof ProgramWeekFormValues, { type: 'server', message }));

                if (outcome.formError) {
                    showSnackbar(outcome.formError, 'error');
                }
            },
        });
    };

    if (isLoading) {
        return (
            <Box sx={sectionStyles}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={sectionStyles}>
            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.WORKING_HOURS_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.WORKING_HOURS_DESCRIPTION)}
                </Typography>

                <ProgramWeekEditor
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    days={current}
                    copy={CLINIC_WEEK_COPY}
                />
            </Paper>

            <Box sx={actionsStyles}>
                {/* Beside a disabled Save, never instead of one. */}
                {!isValid && (
                    <Typography variant="body2" sx={blockedSaveStyles}>
                        {translate(ResourcesIds.FORM_BLOCKED_SAVE)}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    onClick={handleSubmit(onValid)}
                    disabled={!isValid || saveProgram.isPending}
                >
                    {translate(ResourcesIds.WORKING_HOURS_SAVE)}
                </Button>
            </Box>
        </Box>
    );
};
